/**
 * Alles, was im Büro noch liegen geblieben ist — an einer Stelle.
 *
 * ═══ WARUM DAS ZUSAMMENGEHÖRT ═══
 *
 * Vorher standen dieselben Aufgaben auf zwei Bildschirmen: „Belege" zeigte
 * fehlende Papiere und Zuordnungskonflikte, „Nachpflege" die Orte und Kunden,
 * die ein Fahrer unterwegs angelegt hat. Zwei Bildschirme heißen: zweimal
 * hinschauen, und einer wird vergessen. Genau das war der Einwand.
 *
 * Sachlich ist es ohnehin eine einzige Frage: Was muss ich noch anfassen,
 * bevor abgerechnet werden kann? Ob die Antwort ein fehlender Lieferschein
 * ist, ein doppelt angelegtes Kieswerk oder ein Arbeitstag ohne Feierabend,
 * ändert nichts an der Frage.
 *
 * ═══ WARUM ALLES DIESELBE FORM HAT ═══
 *
 * Vier Arten von Aufgabe, aber eine Zeilenform: Was, Wobei, Seit wann, Wer.
 * Erst dadurch lässt sich die Liste sortieren und filtern — und erst dadurch
 * beantwortet sie die Frage „was ist am längsten liegen geblieben", die
 * getrennte Listen nie beantworten konnten.
 *
 * Die vierte Art ist neu: Arbeitstage ohne Zeit. Sie standen bisher nirgends
 * im Büro, obwohl sie unmittelbar Geld kosten — ein Tag ohne Beginn und Ende
 * bringt 0 € Verpflegungspauschale, und der Fahrer merkt es erst auf der
 * Abrechnung.
 */

import { supabase } from '../supabase';
import { Speicherfehler } from './speichern';
import { alsKlarfehler } from './fehler';
import { beleglageText, type Auftragszeile } from './auftraege';
import { heute, tagVerschoben, zeitpunktVon } from './kalender';
import { sortierwerteVergleichen } from './sortierung';

export type Punktart = 'konflikt' | 'belege' | 'fahrt' | 'stammdaten' | 'zeit';

export const PUNKTART: Record<Punktart, { text: string; farbe: 'gut' | 'warn' | 'schlecht' | 'neutral' }> = {
  // Zuordnungskonflikte sind selten, aber teuer: Ein Beleg am falschen Auftrag
  // fällt bei der Abrechnung auf — oder gar nicht.
  konflikt: { text: 'Zuordnung', farbe: 'schlecht' },
  belege: { text: 'Belege', farbe: 'warn' },
  // Eine Tour, deren Ladetag vorbei ist und zu der nie eine Fahrt entstand.
  // Sie stand bisher in KEINER Zusammenstellung — nicht in der Vorausschau,
  // nicht in der Abrechnung, nirgends. Genau die Tour, die niemals abgerechnet
  // wird und die niemandem auffällt.
  fahrt: { text: 'Ohne Fahrt', farbe: 'schlecht' },
  stammdaten: { text: 'Stammdaten', farbe: 'neutral' },
  zeit: { text: 'Arbeitszeit', farbe: 'warn' },
};

export type OffenerPunkt = {
  /** Eindeutig über alle Arten hinweg — die Kennungen allein wären es nicht. */
  schluessel: string;
  art: Punktart;
  /** Die Aufgabe in einem Halbsatz. */
  was: string;
  /** Woran sie hängt: Tour, Ort, Mitarbeiter. */
  wobei: string;
  /** Zusatz, der die Dringlichkeit einordnet. */
  hinweis: string | null;
  /** Wann das entstanden ist — danach wird standardmäßig sortiert. */
  seit: string;
  /** Wohin der Klick führt, falls es einen Ort dafür gibt. */
  ziel: string | null;
  /** Die Rohdaten für die Handlung direkt in der Zeile. */
  auftrag?: Auftragszeile;
  konfliktId?: string;
  /** Mit Stand: Bestätigen und Umbenennen prüfen dagegen (H1.6). */
  stammsatz?: { art: 'ort' | 'kunde'; id: string; name: string; geaendert_am: string };
};

type Rohkonflikt = {
  id: string;
  art: string;
  entitaet: string;
  beschreibung: string | null;
  erkannt_am: string;
};

type Rohnachpflege = {
  art: 'ort' | 'kunde';
  id: string;
  name: string;
  angelegt_durch: string | null;
  verwendungen: number;
  erstellt_am: string;
  geaendert_am: string;
};

type Rohzeit = {
  arbeitstag_id: string;
  mitarbeiter_id: string;
  datum: string;
  beginn: string | null;
  ende: string | null;
};

function tagText(datum: string): string {
  return new Date(datum + 'T12:00:00').toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

/**
 * Alles auf einmal holen.
 *
 * Vier Abfragen parallel statt nacheinander: Die Seite ist der erste Blick des
 * Tages, und vier Serverrunden hintereinander sind der Unterschied zwischen
 * „ist schon da" und „lädt noch".
 */
export async function offenePunkteLaden(): Promise<OffenerPunkt[]> {
  // Örtlicher Kalendertag, nicht UTC: Abends nach 22 Uhr wäre die obere Grenze
  // sonst schon gestern, und die Arbeitstage des laufenden Tages fielen aus der
  // Liste — ausgerechnet in den Stunden, in denen die Fahrer Feierabend machen.
  const bis = heute();
  // Nur der laufende und der Vormonat: Ein Tag ohne Zeit aus dem Frühjahr ist
  // keine Aufgabe mehr, sondern Geschichte. Stünde er hier, wäre die Liste nach
  // einem Jahr unbrauchbar und niemand sähe die frischen Fälle noch.
  const ab = tagVerschoben(-45);

  const [auftraege, konflikte, nachpflege, zeiten, leute] = await Promise.all([
    // Nur die beiden Zustände, um die es hier geht — in der Datenbank
    // ausgewählt, nicht im Browser. Mit `limit(1000)` und ohne Sortierung
    // fielen sonst nach einigen Monaten Pendelverkehr beliebige Touren still
    // heraus, darunter die noch offenen. Aufsteigend nach Ladedatum, damit im
    // Zweifel die am längsten Liegengebliebenen drinbleiben — genau die, um die
    // sich niemand mehr kümmert.
    supabase
      .from('auftrag_uebersicht')
      .select('*')
      .in('zustand', ['ohne_fahrt', 'belege_fehlen'])
      .order('ladedatum', { ascending: true, nullsFirst: false })
      .limit(1000),
    supabase
      .from('sync_konflikt')
      .select('id, art, entitaet, beschreibung, erkannt_am')
      .is('erledigt_am', null)
      .order('erkannt_am', { ascending: false }),
    supabase.from('nachzupflegen').select('*'),
    supabase
      .from('spesentag')
      .select('arbeitstag_id, mitarbeiter_id, datum, beginn, ende')
      .gte('datum', ab)
      .lte('datum', bis)
      .or('beginn.is.null,ende.is.null'),
    supabase.from('mitarbeiter').select('id, vorname, nachname'),
  ]);

  /**
   * ═══ JEDE DER FÜNF ABFRAGEN MUSS GEKLAPPT HABEN ═══
   *
   * Vorher wurde nur die erste geprüft, die anderen vier gingen über `?? []`
   * still ins Leere. Scheitert dann die Abfrage auf `sync_konflikt` oder
   * `spesentag` — abgelaufenes Token, Zeilenregel, hängendes Netz —, meldet
   * dieser Bildschirm nicht etwa einen Fehler, sondern das Gegenteil: „Nichts
   * offen. Das kommt selten vor — jetzt wäre ein guter Moment zum Abrechnen."
   *
   * Eine Entwarnung, die aus einem Fehler entsteht, ist der teuerste Fehler,
   * den eine Aufgabenliste machen kann: Man sieht ihr nicht an, dass sie
   * lügt, und man kommt nie wieder darauf zurück.
   */
  for (const antwort of [auftraege, konflikte, nachpflege, zeiten, leute]) {
    if (antwort.error) throw new Speicherfehler(alsKlarfehler(antwort.error));
  }

  const punkte: OffenerPunkt[] = [];

  for (const roh of (konflikte.data ?? []) as Rohkonflikt[]) {
    punkte.push({
      schluessel: `konflikt:${roh.id}`,
      art: 'konflikt',
      was: 'Eintrag kam zu einem umdisponierten Auftrag',
      wobei: roh.entitaet,
      hinweis: roh.beschreibung,
      seit: roh.erkannt_am,
      ziel: null,
      konfliktId: roh.id,
    });
  }

  for (const auftrag of (auftraege.data ?? []) as Auftragszeile[]) {
    // ═══ TOUREN, ZU DENEN NIE JEMAND GEFAHREN IST ═══
    //
    // Erst ab dem Tag NACH dem Ladetag: Eine Tour, die für nächste Woche
    // eingetragen ist und noch keine Fahrt hat, ist keine Aufgabe, sondern der
    // Normalfall. Ohne diese Grenze stünde hier jede geplante Tour, und die
    // Liste wäre binnen einer Woche unbrauchbar.
    if (
      auftrag.zustand === 'ohne_fahrt' &&
      auftrag.ladedatum !== null &&
      auftrag.ladedatum < bis
    ) {
      punkte.push({
        schluessel: `fahrt:${auftrag.id}`,
        art: 'fahrt',
        was: 'Ladetag vorbei, keine Fahrt eingetragen',
        wobei: `${auftrag.nummer ?? 'Nummer folgt'} · ${auftrag.kunde ?? '—'}`,
        hinweis:
          [auftrag.ladeort, auftrag.entladeort].filter(Boolean).join(' → ') || null,
        seit: auftrag.ladedatum,
        ziel: `/auftrag/${auftrag.id}`,
        auftrag,
      });
      continue;
    }

    // Was gefahren ist, aber ohne Papiere dasteht. Geplante Touren gehören
    // nicht hierher — dort FEHLT nichts, dort ist nur noch nichts passiert.
    if (auftrag.zustand !== 'belege_fehlen') continue;
    punkte.push({
      schluessel: `belege:${auftrag.id}`,
      art: 'belege',
      was: beleglageText(auftrag.beleglage).text,
      wobei: `${auftrag.nummer ?? 'Nummer folgt'} · ${auftrag.kunde ?? '—'}`,
      hinweis: [auftrag.ladeort, auftrag.entladeort].filter(Boolean).join(' → ') || null,
      // Den Zeitstempel ganz, nicht `.slice(0, 10)`: Das schnitte den UTC-Tag
      // ab, und eine Tour von 0:30 Uhr läge einen Tag länger (H1.5).
      seit: auftrag.ladedatum ?? auftrag.erstellt_am,
      ziel: `/auftrag/${auftrag.id}`,
      auftrag,
    });
  }

  for (const roh of (nachpflege.data ?? []) as Rohnachpflege[]) {
    punkte.push({
      schluessel: `stamm:${roh.art}:${roh.id}`,
      art: 'stammdaten',
      was:
        roh.art === 'ort'
          ? 'Ort unterwegs angelegt, noch ungeprüft'
          : 'Kunde unterwegs angelegt, noch ungeprüft',
      wobei: roh.name,
      // Die Verwendungszahl sagt, wie man damit umgeht: Null Verwendungen ist
      // ein Vertipper von gestern, zwölf sind gelebte Wirklichkeit.
      hinweis:
        roh.verwendungen === 0
          ? `Noch nirgends verwendet · von ${roh.angelegt_durch ?? 'unbekannt'}`
          : `${roh.verwendungen} Mal verwendet · von ${roh.angelegt_durch ?? 'unbekannt'}`,
      seit: roh.erstellt_am,
      ziel: null,
      stammsatz: {
        art: roh.art,
        id: roh.id,
        name: roh.name,
        geaendert_am: roh.geaendert_am,
      },
    });
  }

  const name = new Map(
    (
      (leute.data ?? []) as { id: string; vorname: string; nachname: string }[]
    ).map((m) => [m.id, `${m.vorname} ${m.nachname}`]),
  );

  for (const roh of (zeiten.data ?? []) as Rohzeit[]) {
    punkte.push({
      schluessel: `zeit:${roh.arbeitstag_id}`,
      art: 'zeit',
      was: !roh.beginn && !roh.ende
        ? 'Arbeitstag ohne Beginn und Ende'
        : !roh.beginn
          ? 'Arbeitstag ohne Beginn'
          : 'Arbeitstag ohne Feierabend',
      wobei: name.get(roh.mitarbeiter_id) ?? 'Unbekannt',
      hinweis: `${tagText(roh.datum)} · bringt so 0,00 € Verpflegung`,
      seit: roh.datum,
      ziel: '/zeiten',
    });
  }

  // Ältestes zuerst. Was am längsten liegt, ist am ehesten vergessen — und bei
  // Belegen läuft irgendwann die Frist des Kunden.
  // Als Zeitpunkt, nicht als Text: Hier stehen Tage neben Zeitstempeln (H1.5).
  // Über dieselbe Vergleichsregel wie die Tabelle — ein unlesbares Datum
  // (NaN) steht dann hinten, statt die Reihenfolge aller Zeilen zu stören.
  return punkte.sort((a, b) =>
    sortierwerteVergleichen(zeitpunktVon(a.seit), zeitpunktVon(b.seit), false),
  );
}
